import { clip } from './followUpPrompt';

/**
 * The requests a tool writes whole for Otto and starts as a mission at once (`useAskOtto.start`,
 * 12/10/2026: no more chat). Three rules, from the review of that day:
 * - the first line names the subject: it becomes the mission's title in the Bureau's list, where
 *   twenty « Génère un compte-rendu… » could not be told apart;
 * - objects travel by their KEY (principle 12) and their text only as a short excerpt: Otto reads
 *   the whole note, the persona sheet or the post with his tools, and the mission's objective
 *   stays under the server's 4000 characters (`NEW_MISSION_MAX`), where a pasted 6 000-character
 *   persona sheet made every request fail;
 * - nothing asks Otto to open a mission: he reads this inside one. Pure.
 */

const EXCERPT = 1500;

export interface MeetingReportRequest {
  appointmentId?: string | null;
  noteId?: string | null;
  noteType?: string | null;
  contactName: string;
  contactEmail?: string | null;
  accountName?: string | null;
  /** The meeting notes or the note's text: an excerpt goes, the whole is read by its key. */
  notes?: string | null;
}

/** « Générer le CR » of an appointment or of a note. */
export function meetingReportRequest(req: MeetingReportRequest): string {
  const who = req.accountName ? `${req.contactName} (${req.accountName})` : req.contactName;
  const notes = req.notes?.trim() ? clip(req.notes, EXCERPT) : '';
  return [
    `Compte rendu de réunion — ${who}`,
    req.appointmentId ? `- Rendez-vous ID: ${req.appointmentId}` : null,
    req.noteId ? `- Note ID: ${req.noteId}` : null,
    req.noteType ? `- Type de note: ${req.noteType}` : null,
    req.contactEmail ? `- Email contact: ${req.contactEmail}` : null,
    notes ? `\nNotes (extrait) :\n${notes}` : null,
    '',
    req.appointmentId
      ? `Récupère d'abord la transcription Google Meet du rendez-vous, puis rédige le CR et identifie les actions.`
      : `Il n'y a pas de rendez-vous lié : rédige le CR à partir de la note (lis-la en entier par son id) et identifie les actions.`,
  ]
    .filter((line) => line !== null)
    .join('\n');
}

export type BriefKind = 'blog' | 'newsletter' | 'linkedin' | 'prospection';

/** « Préparer avec Otto » of a content editor: the audience goes by its persona id, never pasted. */
export function writingBriefRequest(req: { kind: BriefKind; topic: string; brief: string; personaId: string | null }): string {
  const contentType = req.kind === 'prospection' ? 'sales_email' : req.kind;
  const subject = req.topic.trim() || clip(req.brief, 80);
  return [
    `Brouillon ${req.kind} : ${subject}`,
    req.brief.trim() ? `Brief : ${clip(req.brief, EXCERPT)}` : null,
    `Charge getWritingGuidelines(content_type="${contentType}"${req.personaId ? `, persona_id="${req.personaId}"` : ''}) : ` +
      (req.personaId
        ? 'son « audience » porte la fiche du public retenu.'
        : 'public transversal, un enjeu commun aux publics concernés (son « audience » les résume).'),
    'Présente le public retenu et les références utilisées à part du brouillon. Ne publie et n’envoie rien. ' +
      'Ne remplace aucun contenu existant sans demande explicite.',
  ]
    .filter((line) => line !== null)
    .join('\n');
}
